import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import Login from './pages/Login';
import AdminLogin from './pages/AdminLogin';
import SuperAdminLogin from './pages/SuperAdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import FranchiseDashboard from './pages/FranchiseDashboard';
import GenerateOrder from './pages/GenerateOrder';
import OrderDetail from './pages/OrderDetail';
import LedgerManagement from './pages/LedgerManagement';
import RawMaterialOrder from './pages/RawMaterialOrder';
import FranchiseInventoryPage from './pages/FranchiseInventoryPage';
import FOSLandingPage from './pages/FOSLandingPage';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AuthCallback from './components/AuthCallback';
import NetworkStatus from './components/NetworkStatus';
import UpdateNotification from './components/UpdateNotification';
import MandatoryUpdateModal from './components/MandatoryUpdateModal';
import usePWAConfig from './hooks/usePWAConfig';
import { Toaster } from './components/ui/sonner';
import { LanguageProvider } from './contexts/LanguageContext';
import './App.css';

/**
 * Detect which subdomain the app is running on
 * Production domains:
 * - www.getfos.in or getfos.in → Landing page
 * - superadmin.getfos.in → Super Admin portal
 * - billing.getfos.in → Admin & Franchise portal
 * - localhost / onrender.com → Development (all routes available)
 */
const getSubdomain = () => {
  const hostname = window.location.hostname;
  
  // Development mode - allow all routes (localhost, Emergent preview, Render preview)
  if (hostname === 'localhost' || 
      hostname === '127.0.0.1' || 
      hostname.includes('preview.emergentagent.com') ||
      hostname.includes('.onrender.com')) {
    return 'dev';
  }
  
  // Production subdomain detection
  if (hostname.startsWith('superadmin.')) return 'superadmin';
  if (hostname.startsWith('billing.')) return 'billing';
  if (hostname.startsWith('www.') || hostname === 'getfos.in') return 'www';
  
  // Default to www for unknown subdomains
  return 'www';
};

// Router component that handles subdomain-based and auth callback routing
const AppRouter = ({ user, setUser, loading, subdomain }) => {
  const location = useLocation();
  
  // Apply PWA configuration based on current route
  usePWAConfig();
  
  // Check URL fragment for session_id (Google OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback setUser={setUser} />;
  }

  // Show loading screen while checking for saved user
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-secondary">Loading...</p>
        </div>
      </div>
    );
  }

  // Helper function to determine redirect path based on role
  const getRedirectPath = (userRole) => {
    if (userRole === 'super_admin') return '/super-admin';
    if (userRole === 'admin') return '/admin';
    if (userRole === 'franchise') return '/franchise';
    return '/';
  };

  // ============================================
  // SUBDOMAIN: www.getfos.in (Landing Page Only)
  // ============================================
  if (subdomain === 'www') {
    return (
      <Routes>
        <Route path="*" element={<FOSLandingPage />} />
      </Routes>
    );
  }

  // ============================================
  // SUBDOMAIN: superadmin.getfos.in (Super Admin Only)
  // ============================================
  if (subdomain === 'superadmin') {
    return (
      <Routes>
        <Route path="/" element={user?.role === 'super_admin' ? <Navigate to="/super-admin" /> : <SuperAdminLogin setUser={setUser} />} />
        <Route path="/login" element={user?.role === 'super_admin' ? <Navigate to="/super-admin" /> : <SuperAdminLogin setUser={setUser} />} />
        <Route path="/super-admin/*" element={user?.role === 'super_admin' ? <SuperAdminDashboard user={user} setUser={setUser} /> : <Navigate to="/" />} />
        {/* Catch all - redirect to login */}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    );
  }

  // ============================================
  // SUBDOMAIN: billing.getfos.in (Admin & Franchise)
  // ============================================
  if (subdomain === 'billing') {
    return (
      <Routes>
        {/* Admin Routes - billing.getfos.in/admin */}
        <Route path="/admin" element={user?.role === 'admin' ? <Navigate to="/admin/dashboard" /> : <AdminLogin setUser={setUser} />} />
        <Route path="/admin/login" element={user?.role === 'admin' ? <Navigate to="/admin/dashboard" /> : <AdminLogin setUser={setUser} />} />
        <Route path="/admin/dashboard" element={user?.role === 'admin' ? <AdminDashboard user={user} setUser={setUser} /> : <Navigate to="/admin" />} />
        <Route path="/admin/*" element={user?.role === 'admin' ? <AdminDashboard user={user} setUser={setUser} /> : <Navigate to="/admin" />} />
        
        {/* Admin OAuth callback */}
        <Route path="/admin-dashboard" element={user?.role === 'admin' ? <Navigate to="/admin/dashboard" /> : <AdminLogin setUser={setUser} />} />
        
        {/* Password Reset Routes */}
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        
        {/* Franchise Routes - billing.getfos.in/franchise */}
        <Route path="/franchise" element={user?.role === 'franchise' ? <Navigate to="/franchise/dashboard" /> : <Login setUser={setUser} />} />
        <Route path="/franchise/login" element={user?.role === 'franchise' ? <Navigate to="/franchise/dashboard" /> : <Login setUser={setUser} />} />
        <Route path="/franchise/dashboard" element={user?.role === 'franchise' ? <FranchiseDashboard user={user} setUser={setUser} /> : <Navigate to="/franchise" />} />
        <Route path="/franchise/*" element={user?.role === 'franchise' ? <FranchiseDashboard user={user} setUser={setUser} /> : <Navigate to="/franchise" />} />
        
        {/* Franchise Sub-routes */}
        <Route path="/generate-order" element={user?.role === 'franchise' ? <GenerateOrder user={user} /> : <Navigate to="/franchise" />} />
        <Route path="/ledger-management" element={user?.role === 'franchise' ? <LedgerManagement user={user} /> : <Navigate to="/franchise" />} />
        <Route path="/raw-material-order" element={user?.role === 'franchise' ? <RawMaterialOrder user={user} /> : <Navigate to="/franchise" />} />
        <Route path="/franchise/inventory" element={user?.role === 'franchise' ? <FranchiseInventoryPage user={user} /> : <Navigate to="/franchise" />} />
        <Route path="/order/:orderId" element={user ? <OrderDetail user={user} /> : <Navigate to="/franchise" />} />
        
        {/* Root URL (billing.getfos.in/) redirects to /admin */}
        <Route path="/" element={
          user?.role === 'admin' ? <Navigate to="/admin/dashboard" /> :
          user?.role === 'franchise' ? <Navigate to="/franchise/dashboard" /> :
          <Navigate to="/admin" />
        } />
        
        {/* Catch all - redirect to admin login (default for billing subdomain) */}
        <Route path="*" element={<Navigate to="/admin" />} />
      </Routes>
    );
  }

  // ============================================
  // DEVELOPMENT MODE (localhost) - All Routes
  // ============================================
  return (
    <Routes>
      {/* Public FOS Landing Page */}
      <Route path="/fos" element={<FOSLandingPage />} />
      
      {/* Main Login Routes (Franchise) */}
      <Route path="/" element={user ? <Navigate to={getRedirectPath(user.role)} /> : <Login setUser={setUser} />} />
      <Route path="/login" element={user ? <Navigate to={getRedirectPath(user.role)} /> : <Login setUser={setUser} />} />
      <Route path="/franchiselogin" element={user ? <Navigate to={getRedirectPath(user.role)} /> : <Login setUser={setUser} />} />
      
      {/* Admin Login Routes */}
      <Route path="/admin-login" element={user ? <Navigate to={getRedirectPath(user.role)} /> : <AdminLogin setUser={setUser} />} />
      <Route path="/adminlogin" element={user ? <Navigate to={getRedirectPath(user.role)} /> : <AdminLogin setUser={setUser} />} />
      
      {/* Admin Dashboard Routes - Google OAuth lands here */}
      <Route path="/admin-dashboard" element={user?.role === 'admin' ? <Navigate to="/admin" /> : <AdminLogin setUser={setUser} />} />
      
      {/* Password Reset Routes */}
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      
      {/* Super Admin Routes */}
      <Route path="/super-admin-login" element={user?.role === 'super_admin' ? <Navigate to="/super-admin" /> : <SuperAdminLogin setUser={setUser} />} />
      <Route path="/hq" element={user?.role === 'super_admin' ? <Navigate to="/super-admin" /> : <SuperAdminLogin setUser={setUser} />} />
      <Route path="/super-admin/*" element={user?.role === 'super_admin' ? <SuperAdminDashboard user={user} setUser={setUser} /> : <Navigate to="/super-admin-login" />} />
      
      {/* Admin Dashboard Routes */}
      <Route path="/admin/*" element={user?.role === 'admin' ? <AdminDashboard user={user} setUser={setUser} /> : <Navigate to="/adminlogin" />} />
      
      {/* Franchise Dashboard Routes */}
      <Route path="/franchise/*" element={user?.role === 'franchise' ? <FranchiseDashboard user={user} setUser={setUser} /> : <Navigate to="/" />} />
      <Route path="/generate-order" element={user?.role === 'franchise' ? <GenerateOrder user={user} /> : <Navigate to="/" />} />
      <Route path="/ledger-management" element={user?.role === 'franchise' ? <LedgerManagement user={user} /> : <Navigate to="/" />} />
      <Route path="/raw-material-order" element={user?.role === 'franchise' ? <RawMaterialOrder user={user} /> : <Navigate to="/" />} />
      <Route path="/franchise/inventory" element={user?.role === 'franchise' ? <FranchiseInventoryPage user={user} /> : <Navigate to="/" />} />
      
      {/* Order Detail (accessible by all authenticated users) */}
      <Route path="/order/:orderId" element={user ? <OrderDetail user={user} /> : <Navigate to="/" />} />
    </Routes>
  );
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const subdomain = getSubdomain();
  
  // Check if running in native Capacitor app (Android)
  const isNativeApp = Capacitor.isNativePlatform();
  const [updateCheckComplete, setUpdateCheckComplete] = useState(!isNativeApp);

  // Log which subdomain mode we're in
  useEffect(() => {
    console.log('[App] Running in subdomain mode:', subdomain);
    console.log('[App] Is native app:', isNativeApp);
  }, [subdomain, isNativeApp]);

  // Load user from localStorage on app start
  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Load user from localStorage (critical for page reload)
        const savedUser = localStorage.getItem('user');
        if (savedUser) {
          try {
            const parsedUser = JSON.parse(savedUser);
            setUser(parsedUser);
            console.log('[App] User loaded from localStorage:', parsedUser.role);
          } catch (parseError) {
            console.error('[App] Error parsing saved user:', parseError);
            localStorage.removeItem('user');
          }
        }
      } catch (error) {
        console.error('[App] Error initializing app:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeApp();
  }, []);

  // Save user to localStorage whenever it changes
  useEffect(() => {
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    } else {
      localStorage.removeItem('user');
    }
  }, [user]);

  // Show loading screen while checking for saved user
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-secondary">Loading...</p>
        </div>
      </div>
    );
  }

  // If native app and update check not complete, show mandatory update modal
  if (isNativeApp && !updateCheckComplete) {
    return (
      <div className="App min-h-screen bg-background">
        <MandatoryUpdateModal onUpdateComplete={() => setUpdateCheckComplete(true)} />
        <Toaster position="top-center" richColors />
      </div>
    );
  }

  return (
    <div className="App min-h-screen bg-background">
      <LanguageProvider>
        <UpdateNotification />
        {/* NetworkStatus bar temporarily disabled - will be used in upcoming feature */}
        {/* {user?.role === 'franchise' && <NetworkStatus />} */}
        <BrowserRouter>
          <AppRouter user={user} setUser={setUser} loading={loading} subdomain={subdomain} />
        </BrowserRouter>
        <Toaster position="top-center" richColors />
      </LanguageProvider>
    </div>
  );
}

export default App;
